<template>
  <div class="training-calendar">
    <!-- Header -->
    <div class="tc-header">
      <h4 class="tc-title">📅 {{ t('calendar.trainingReservations') }}</h4>

      <div class="tc-toolbar">
        <div class="btn-group">
          <button
            v-for="v in views"
            :key="v"
            class="btn"
            :class="{ active: view === v }"
            @click="setView(v)"
          >
            {{ v.toUpperCase() }}
          </button>
        </div>

        <div class="nav-group">
          <button class="btn" @click="goPrev">
            <i class="fas fa-chevron-left" />
          </button>

          <span class="period-label">{{ currentLabel }}</span>

          <button class="btn" @click="goNext">
            <i class="fas fa-chevron-right" />
          </button>

          <button class="btn outline" @click="goToday">
            {{ t('calendar.buttons.today') }}
          </button>

          <button class="btn primary" @click="createTestReservation">
            Test Reservation
          </button>
        </div>
      </div>
    </div>

    <!-- Légende -->
    <div class="legend">
      <span class="dot dot-pending"></span>
      {{ t('calendar.messages.pending') }}

      <span class="dot dot-confirmed"></span>
      {{ t('calendar.messages.confirmed') }}

      <span class="dot dot-cancelled"></span>
      {{ t('calendar.messages.cancelled') }}
    </div>

    <!-- Calendrier -->
    <vue-cal
      class="tc-cal"
      style="height: 620px"
      :hide-view-selector="true"
      :default-view="view"
      :view="view"
      :selected-date="currentDate"
      :events="events"
      :time="true"
      :time-from="8 * 60"
      :time-to="18 * 60"
      :time-step="30"
      :time-cell-height="40"
      :disable-days="[]"
      @cell-click="onDayClick"
      @event-click="onEventClick"
      @ready="onCalendarReady"
      @view-change="onViewChange"
    />

    <!-- Formulaire -->
    <div v-if="showForm" class="reservation-form card">
      <div class="form-head">
        <h5 class="m-0">
          {{ t('calendar.buttons.newReservation') }}
        </h5>
        <button class="btn outline" @click="showForm = false">
          {{ t('calendar.buttons.close') }}
        </button>
      </div>

      <form @submit.prevent="submitReservation" class="form-body">
        <label>
          {{ t('calendar.projectType') }}
          <select v-model="form.project_type" required>
            <option value="internal">
              {{ t('calendar.internal') }}
            </option>
            <option value="external">
              {{ t('calendar.external') }}
            </option>
          </select>
        </label>

        <div class="row-2">
          <label>
            {{ t('calendar.startDate') }}
            <input type="date" v-model="form.start_date" required />
          </label>
          <label>
            {{ t('calendar.endDate') }}
            <input type="date" v-model="form.end_date" required />
          </label>
        </div>

        <div class="row-2">
          <label>
            {{ t('calendar.startTime') }}
            <input type="time" v-model="form.start_time" required />
          </label>
          <label>
            {{ t('calendar.endTime') }}
            <input type="time" v-model="form.end_time" required />
          </label>
        </div>

        <label>
          {{ t('calendar.messages.remarks') }}
          <input
            type="text"
            v-model="form.remarks"
            :placeholder="t('calendar.enterRemarks')"
            required
          />
        </label>

        <label>
          {{ t('calendar.messages.numberOfPersons') }}
          <input
            type="number"
            v-model.number="form.number_of_persons"
            min="1"
            required
          />
        </label>

        <!-- Optionnel (le backend force pending à la création) -->
        <label>
          {{ t('calendar.messages.reservationStatus') }}
          <select v-model="form.status" disabled>
            <option value="pending">
              {{ t('calendar.messages.pending') }}
            </option>
            <option value="confirmed">
              {{ t('calendar.messages.confirmed') }}
            </option>
            <option value="cancelled">
              {{ t('calendar.messages.cancelled') }}
            </option>
          </select>
        </label>

        <div class="actions">
          <button type="button" class="btn outline" @click="showForm = false">
            {{ t('calendar.buttons.cancel') }}
          </button>

          <button type="submit" class="btn primary" :disabled="loading">
            {{ loading ? '...' : t('calendar.buttons.save') }}
          </button>
        </div>
      </form>
    </div>

    <!-- Event actions -->
    <div v-if="activeEvent" class="event-actions card">
      <div class="form-head">
        <h6 class="m-0">
          {{ t('calendar.messages.reservation') }} {{ activeEvent.id }}
        </h6>

        <button class="btn outline" @click="activeEvent = null">
          {{ t('calendar.buttons.close') }}
        </button>
      </div>

      <div class="ea-body">
        <p class="ea-line">
          <strong>{{ t('calendar.messages.when') }}</strong>
          {{ formatDateTime(activeEvent.start) }} →
          {{ formatDateTime(activeEvent.end) }}
        </p>

        <p class="ea-line">
          <strong>{{ t('calendar.messages.type') }}:</strong>
          {{ activeEvent.project_type }}
        </p>

        <p class="ea-line">
          <strong>{{ t('calendar.messages.reservationStatus') }}:</strong>
          {{ activeEvent.status }}
        </p>

        <p class="ea-line">
          <strong>{{ t('calendar.messages.remarks') }}</strong>
          {{ activeEvent.title }}
        </p>

        <div class="actions">
          <button
            class="btn outline"
            @click="setStatus(activeEvent.id, 'confirmed')"
            :disabled="loading"
          >
            {{ t('calendar.buttons.confirm') }}
          </button>

          <button
            class="btn outline"
            @click="setStatus(activeEvent.id, 'cancelled')"
            :disabled="loading"
          >
            {{ t('calendar.buttons.cancel') }}
          </button>

          <button
            class="btn danger"
            @click="deleteReservation(activeEvent.id)"
            :disabled="loading"
          >
            {{ t('calendar.buttons.delete') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import axios from '@/api'
import VueCal from 'vue-cal'
import 'vue-cal/dist/vuecal.css'

const { t } = useI18n({ useScope: 'global' })

/** Endpoints (NE PAS mettre /api ici si axios a déjà baseURL .../api) */
const BASE = '/training-reservations'
const PERIOD_URL = '/training-reservations/period'
const STATUS_URL = (id, status) => `${BASE}/${id}/status/${status}`

/** State */
const rawReservations = ref([])
const events = ref([])
const showForm = ref(false)
const activeEvent = ref(null)
const loading = ref(false)

const views = ['year', 'month', 'week', 'day']
const view = ref('week')
const currentDate = ref(new Date())

const form = ref({
  start_date: '',
  end_date: '',
  start_time: '08:00',
  end_time: '10:00',
  project_type: 'internal',
  remarks: '',
  number_of_persons: 1,
  status: 'pending'
})

/** Utils */
const pad2 = n => String(n).padStart(2, '0')
const fmtDate = d => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
const fmtMonth = d => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`
const fmtYear = d => `${d.getFullYear()}`
const onlyHHmm = t => (t || '').slice(0, 5) // "08:00:00" -> "08:00"

const startOf = {
  day(d) {
    const x = new Date(d)
    x.setHours(0, 0, 0, 0)
    return x
  },
  month(d) {
    const x = new Date(d.getFullYear(), d.getMonth(), 1)
    x.setHours(0, 0, 0, 0)
    return x
  },
  year(d) {
    const x = new Date(d.getFullYear(), 0, 1)
    x.setHours(0, 0, 0, 0)
    return x
  }
}
const endOf = {
  day(d) {
    const x = new Date(d)
    x.setHours(23, 59, 59, 999)
    return x
  },
  month(d) {
    const x = new Date(d.getFullYear(), d.getMonth() + 1, 0)
    x.setHours(23, 59, 59, 999)
    return x
  },
  year(d) {
    const x = new Date(d.getFullYear(), 11, 31)
    x.setHours(23, 59, 59, 999)
    return x
  }
}

function weekRange(d) {
  const day = new Date(d)
  const dow = day.getDay() || 7 // 1..7 (Mon..Sun)
  const start = new Date(day)
  start.setDate(day.getDate() - (dow - 1))
  start.setHours(0, 0, 0, 0)

  const end = new Date(start)
  end.setDate(start.getDate() + 6)
  end.setHours(23, 59, 59, 999)

  return { start, end }
}

function getWeekNumber(d) {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()))
  const dayNum = date.getUTCDay() || 7
  date.setUTCDate(date.getUTCDate() + 4 - dayNum)
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1))
  return Math.ceil((((date - yearStart) / 86400000) + 1) / 7)
}

const currentLabel = computed(() => {
  const d = currentDate.value
  if (view.value === 'year') return fmtYear(d)
  if (view.value === 'month') return fmtMonth(d)
  if (view.value === 'week') {
    const { start } = weekRange(d)
    return `Week ${getWeekNumber(d)} (${start.toLocaleString('default', { month: 'long' })} ${start.getFullYear()})`
  }
  return fmtDate(d)
})

/** Helpers API */
function normalizeArrayResponse(payload) {
  // /period => { data: [...] }
  if (payload?.data && Array.isArray(payload.data)) return payload.data
  // index paginé => { data: [...] , current_page, ...}
  if (Array.isArray(payload)) return payload
  if (payload?.items && Array.isArray(payload.items)) return payload.items

  // fallback (rare)
  const arrays = Object.values(payload || {}).filter(Array.isArray)
  return arrays[0] || []
}

async function fetchPeriod(params) {
  const res = await axios.get(PERIOD_URL, { params })
  return normalizeArrayResponse(res.data)
}

/** Fallback si /period ne marche pas (ex: route cache serveur) */
async function fetchAllReservationsFallback() {
  const all = []
  let page = 1
  let last = 1

  do {
    const res = await axios.get(BASE, { params: { page } })
    const payload = res?.data
    const items = normalizeArrayResponse(payload)

    all.push(...items)
    last = payload?.last_page || payload?.meta?.last_page || 1
    page += 1
  } while (page <= last)

  return all
}

function uniqueById(list) {
  const map = new Map()
  list.forEach(item => {
    if (item?.id != null) map.set(item.id, item)
  })
  return [...map.values()]
}

/** Charger selon la vue courante */
async function fetchReservationsForCurrentView() {
  loading.value = true
  try {
    const d = new Date(currentDate.value)
    let rows = []

    if (view.value === 'day') {
      rows = await fetchPeriod({ date: fmtDate(d) })
    } else if (view.value === 'month') {
      rows = await fetchPeriod({ month: fmtMonth(d) }) // "YYYY-MM"
    } else if (view.value === 'year') {
      rows = await fetchPeriod({ year: fmtYear(d) })
    } else {
      // week => backend n'a pas "week", on récupère 1 ou 2 mois puis on filtre localement
      const { start, end } = weekRange(d)
      const m1 = fmtMonth(start)
      const m2 = fmtMonth(end)

      const r1 = await fetchPeriod({ month: m1 })
      if (m1 === m2) {
        rows = r1
      } else {
        const r2 = await fetchPeriod({ month: m2 })
        rows = uniqueById([...r1, ...r2])
      }
    }

    rawReservations.value = rows
    buildEventsForView()
  } catch (err) {
    console.error('❌ fetchReservationsForCurrentView error:', err?.response?.status, err?.response?.data || err)

    // fallback automatique si /period renvoie 404
    if (err?.response?.status === 404) {
      try {
        console.warn('⚠️ /period indisponible, fallback vers GET /training-reservations (pagination)')
        const all = await fetchAllReservationsFallback()
        rawReservations.value = all
        buildEventsForView()
        return
      } catch (fallbackErr) {
        console.error('❌ Fallback index failed:', fallbackErr?.response?.status, fallbackErr?.response?.data || fallbackErr)
      }
    }

    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.period?.[0] ||
      'Erreur lors de la récupération des réservations.'
    alert(msg)
  } finally {
    loading.value = false
  }
}

/** Construire les events pour la vue courante (filtre local) */
const inRange = (s, e, a, b) => s <= b && e >= a

function toDateTime(dateStr, timeStr, fallback = '00:00') {
  const d = dateStr || ''
  const t = onlyHHmm(timeStr) || fallback
  const date = new Date(`${d}T${t}`)
  return date
}

const buildEventsForView = () => {
  let a, b
  const d = new Date(currentDate.value)

  if (view.value === 'year') {
    a = startOf.year(d)
    b = endOf.year(d)
  } else if (view.value === 'month') {
    a = startOf.month(d)
    b = endOf.month(d)
  } else if (view.value === 'week') {
    const r = weekRange(d)
    a = r.start
    b = r.end
  } else {
    a = startOf.day(d)
    b = endOf.day(d)
  }

  const mapped = (rawReservations.value || [])
    .map(r => {
      const s = toDateTime(r.start_date || r.startDate, r.start_time || r.startTime, '00:00')
      const e = toDateTime(r.end_date || r.endDate, r.end_time || r.endTime, '23:59')

      if (isNaN(s.getTime()) || isNaN(e.getTime())) return null

      const status = r.status || 'pending'
      const booked = status === 'pending' || status === 'confirmed'

      return {
        id: r.id,
        start: s,
        end: e,
        title: r.remarks || '(sans remarque)',
        content: `${r.project_type || 'N/A'} · ${r.number_of_persons || 0} pers.`,
        class: `${booked ? 'booked' : 'cancelled'} ${status}`,
        status,
        project_type: r.project_type,
        number_of_persons: r.number_of_persons,
        remarks: r.remarks,
        _s: s,
        _e: e
      }
    })
    .filter(Boolean)

  events.value = mapped.filter(ev => inRange(ev._s, ev._e, a, b))
}

/** Navigation / changement de vue */
const setView = async v => {
  view.value = v
  await fetchReservationsForCurrentView()
}

const goToday = async () => {
  currentDate.value = new Date()
  await fetchReservationsForCurrentView()
}

const goPrev = async () => {
  const d = new Date(currentDate.value)
  if (view.value === 'year') d.setFullYear(d.getFullYear() - 1)
  else if (view.value === 'month') d.setMonth(d.getMonth() - 1)
  else if (view.value === 'week') d.setDate(d.getDate() - 7)
  else d.setDate(d.getDate() - 1)

  currentDate.value = d
  await fetchReservationsForCurrentView()
}

const goNext = async () => {
  const d = new Date(currentDate.value)
  if (view.value === 'year') d.setFullYear(d.getFullYear() + 1)
  else if (view.value === 'month') d.setMonth(d.getMonth() + 1)
  else if (view.value === 'week') d.setDate(d.getDate() + 7)
  else d.setDate(d.getDate() + 1)

  currentDate.value = d
  await fetchReservationsForCurrentView()
}

/** Interactions calendrier */
const onDayClick = payload => {
  const clicked = payload?.date instanceof Date ? payload.date : payload instanceof Date ? payload : null
  if (!clicked) return

  const selected = fmtDate(clicked)
  form.value = {
    start_date: selected,
    end_date: selected,
    start_time: '08:00',
    end_time: '10:00',
    project_type: 'internal',
    remarks: '',
    number_of_persons: 1,
    status: 'pending'
  }
  showForm.value = true
}

const onEventClick = ({ event }) => {
  activeEvent.value = event
}

const onCalendarReady = () => {
  // noop (debug si besoin)
}

const onViewChange = async payload => {
  // payload peut contenir { view, startDate, endDate, ... } selon version vue-cal
  if (payload?.view && views.includes(payload.view)) {
    view.value = payload.view
  }
  if (payload?.startDate) {
    const d = new Date(payload.startDate)
    if (!isNaN(d.getTime())) currentDate.value = d
  }
  await fetchReservationsForCurrentView()
}

/** CRUD */
const submitReservation = async () => {
  try {
    loading.value = true

    if (!form.value.start_date || !form.value.end_date) {
      throw new Error('Start date and end date are required')
    }
    if (!form.value.start_time || !form.value.end_time) {
      throw new Error('Start time and end time are required')
    }

    const payload = {
      start_date: form.value.start_date,
      end_date: form.value.end_date,
      start_time: onlyHHmm(form.value.start_time) || '08:00',
      end_time: onlyHHmm(form.value.end_time) || '10:00',
      project_type: form.value.project_type,
      remarks: form.value.remarks,
      number_of_persons: form.value.number_of_persons
      // status non envoyé: backend force "pending"
    }

    await axios.post(BASE, payload)

    showForm.value = false
    form.value = {
      start_date: '',
      end_date: '',
      start_time: '08:00',
      end_time: '10:00',
      project_type: 'internal',
      remarks: '',
      number_of_persons: 1,
      status: 'pending'
    }

    await fetchReservationsForCurrentView()
    alert('Reservation created successfully!')
  } catch (err) {
    console.error('❌ submitReservation:', err?.response?.status, err?.response?.data || err)
    const errorMessage =
      err?.response?.data?.message ||
      err?.response?.data?.errors?.start_date?.[0] ||
      err?.response?.data?.errors?.end_date?.[0] ||
      err?.message ||
      'Failed to save reservation'
    alert(errorMessage)
  } finally {
    loading.value = false
  }
}

const setStatus = async (id, status) => {
  try {
    loading.value = true

    // IMPORTANT: PUT (ta route backend de ce groupe est en PUT)
    await axios.put(STATUS_URL(id, status))

    activeEvent.value = null
    await fetchReservationsForCurrentView()
  } catch (err) {
    console.error('❌ setStatus:', err?.response?.status, err?.response?.data || err)

    // fallback si certains rôles tombent sur l'autre route PATCH
    if (err?.response?.status === 405) {
      try {
        await axios.patch(STATUS_URL(id, status))
        activeEvent.value = null
        await fetchReservationsForCurrentView()
        return
      } catch (e2) {
        console.error('❌ PATCH fallback failed:', e2?.response?.status, e2?.response?.data || e2)
      }
    }

    alert(err?.response?.data?.message || 'Failed to change status')
  } finally {
    loading.value = false
  }
}

const deleteReservation = async id => {
  if (!confirm('Delete this reservation?')) return

  try {
    loading.value = true
    await axios.delete(`${BASE}/${id}`)
    activeEvent.value = null
    await fetchReservationsForCurrentView()
  } catch (err) {
    console.error('❌ deleteReservation:', err?.response?.status, err?.response?.data || err)
    alert(err?.response?.data?.message || 'Failed to delete reservation')
  } finally {
    loading.value = false
  }
}

const createTestReservation = async () => {
  try {
    loading.value = true
    const currentDateStr = fmtDate(currentDate.value)

    const testPayload = {
      start_date: currentDateStr,
      end_date: currentDateStr,
      start_time: '09:00',
      end_time: '10:30',
      project_type: 'internal',
      remarks: 'Réservation de test - Formation',
      number_of_persons: 3
    }

    await axios.post(BASE, testPayload)
    await fetchReservationsForCurrentView()

    alert(`Réservation de test créée pour le ${currentDateStr}!`)
  } catch (err) {
    console.error('❌ createTestReservation:', err?.response?.status, err?.response?.data || err)
    alert(err?.response?.data?.message || 'Échec création réservation test')
  } finally {
    loading.value = false
  }
}

const formatDateTime = dt => {
  const d = dt instanceof Date ? dt : new Date(dt)
  if (isNaN(d.getTime())) return '-'

  const y = d.getFullYear()
  const m = pad2(d.getMonth() + 1)
  const day = pad2(d.getDate())
  const hh = pad2(d.getHours())
  const mm = pad2(d.getMinutes())
  return `${y}-${m}-${day} ${hh}:${mm}`
}

onMounted(async () => {
  await fetchReservationsForCurrentView()
})
</script>

<style scoped>
.training-calendar {
  position: relative;
  padding: 12px;
}

/* Header */
.tc-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 10px;
}
.tc-title {
  margin: 0;
  font-weight: 700;
  color: #2b2b2b;
}
.tc-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
}

/* Buttons */
.btn {
  border: 1px solid #e0e0e0;
  background: #fff;
  color: #333;
  padding: 8px 12px;
  border-radius: 10px;
  cursor: pointer;
  transition: 0.15s ease;
}
.btn:hover {
  background: #f5f5f5;
}
.btn.active {
  background: #b03a45;
  color: #fff;
  border-color: #b03a45;
}
.btn.primary {
  background: #b03a45;
  border-color: #b03a45;
  color: #fff;
}
.btn.outline {
  background: transparent;
  color: #666;
  border: 1px solid #d7d7d7;
}
.btn.danger {
  background: #ef4444;
  color: #fff;
  border-color: #ef4444;
}
.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Nav/Views */
.btn-group {
  display: flex;
  gap: 8px;
}
.nav-group {
  display: flex;
  align-items: center;
  gap: 8px;
}
.period-label {
  font-weight: 600;
  color: #444;
  min-width: 190px;
  text-align: center;
}

/* Legend */
.legend {
  display: flex;
  gap: 14px;
  align-items: center;
  margin: 8px 0 12px;
  color: #555;
}
.legend .dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  display: inline-block;
  margin-right: 6px;
}
.dot-pending {
  background: #f59e0b;
}
.dot-confirmed {
  background: #b03a45;
}
.dot-cancelled {
  background: #ccc;
}

/* Calendar wrapper */
.tc-cal :deep(.vuecal__header) {
  border-radius: 8px 8px 0 0;
}
.tc-cal {
  border: 1px solid #eee;
  border-radius: 14px;
  overflow: hidden;
}

/* Forms & panels */
.card {
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 14px;
  box-shadow: 0 8px 22px rgba(0, 0, 0, 0.07);
}
.reservation-form {
  position: absolute;
  top: 120px;
  right: 12px;
  width: 360px;
  z-index: 10;
}
.event-actions {
  position: absolute;
  top: 120px;
  left: 12px;
  width: 360px;
  z-index: 10;
}
.form-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #eee;
}

.form-body,
.ea-body {
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.form-body .row-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}
.form-body label {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 13px;
  color: #555;
}
.form-body input,
.form-body select {
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 14px;
}
.ea-line {
  margin: 0;
  color: #555;
}
.actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}
</style>

<!-- Styles non scoped pour vue-cal -->
<style>
/* Créneaux réservés (déjà pris) → bordeaux strié */
.vuecal__event.booked {
  background: repeating-linear-gradient(
    45deg,
    rgba(176, 58, 69, 0.85),
    rgba(176, 58, 69, 0.85) 8px,
    rgba(176, 58, 69, 0.65) 8px,
    rgba(176, 58, 69, 0.65) 16px
  ) !important;
  color: #fff !important;
  border: none !important;
}

/* annulées → pales */
.vuecal__event.cancelled {
  background: #eee !important;
  color: #888 !important;
  border: 1px dashed #ccc !important;
  opacity: 0.7;
}
</style>